import React, { useEffect, useState } from 'react';
import ShareButton from '../Button/ShareButton';
import Toast from '../Common/Toast';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { likeCountState, liveUserCountState } from '../../recoil/chatCount/chatCountAtom';
import { otpModalState } from '../../recoil/otp/otpModalAtom';
import { useNavigate } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import useDialog from '../../hooks/useDialog';
import ERROR_CODES from '../../constants/errorCodes';
import { playerIsPlayingState } from '../../recoil/player/playerIsPlayingAtom';
import * as Sentry from '@sentry/react';
import { countryState } from '../../recoil/country/countryAtom';

const VideoInfoWrapper = ({ videoType, clipInfo, liveClipInfo, id }) => {
  const [isToastShow, setIsToastShow] = useState(false);
  const { t } = useTranslation();
  const likeCount = useRecoilValue(likeCountState);
  const liveUserCount = useRecoilValue(liveUserCountState);
  const [localLikeCount, setLocalLikeCount] = useState(null);
  const accessToken = localStorage.getItem('access_token');
  const api = useApi();
  const navigate = useNavigate();
  const setOtpLogin = useSetRecoilState(otpModalState);
  const type_id = id;

  // 240214: VideoLiveDetail 에서 이전
  const countryData = useRecoilValue(countryState);
  const [playable, setPlayable] = useState(false);
  const apiV1 = useApi('v1');
  const { showDialog, hideDialog } = useDialog();
  const currentPlayerIsPlayingState = useRecoilValue(playerIsPlayingState);

  const clearSession = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('exp');
    Sentry.setUser(null);
  };

  const checkPreviewAndLimit = () => {
    if (clipInfo?.preview && !playable) {
      // 스트리밍 제한 걸린 상태
      if (clipInfo?.is_play_limit) {
        handleClickRestrictedMsgDialog();
      } else {
        handleClickAnotherDeviceDialog();
      }
    }
  };

  // 재생 가능한 디바이스 초기화
  const resetPlayable = async () => {
    try {
      const response = await apiV1.delete(`user/device/playable?country=${countryData?.country}`, {
        headers: {
          Authorization: accessToken,
        },
      });
      console.log(response);
    } catch (error) {
      console.log(error);
    }
  };

  // 다른 디바이스 재생 확인 컨펌
  const handleClickAnotherDeviceDialog = () => {
    showDialog({
      dialogType: 'ConfirmDialog',
      dialogProps: {
        title: t('video_playing_another_device_title'),
        message: t('video_playing_another_device_msg'),
        confirmText: t('confirm'),
        cancelText: t('cancel'),
        handleConfirm: () => {
          resetPlayable();
          hideDialog();
        },
        handleClose: () => {
          console.log('Close!');
        },
      },
    });
  };

  const handleClickRestrictedMsgDialog = () => {
    showDialog({
      dialogType: 'AlertDialog',
      dialogProps: {
        title: t('video_streaming_restricted_msg'),
        confirmText: t('confirm'),
        handleConfirm: () => {
          console.log('Confirm!');
        },
      },
    });
  };

  useEffect(() => {
    if (clipInfo) {
      checkPreviewAndLimit();
    }
  }, [clipInfo]);

  // 재생 가능 여부 확인(해당 토큰이 이미 재생 중이면 true)
  useEffect(() => {
    const checkPlayable = async () => {
      if (countryData && countryData.country && accessToken) {
        try {
          const response = await apiV1.get(`user/device/playable?country=${countryData?.country}`, {
            headers: {
              Authorization: accessToken,
            },
          });

          const fetchData = response?.data?.playable;
          setPlayable(fetchData);
        } catch (error) {
          console.log(error);
        }
      }
    };

    if (currentPlayerIsPlayingState) {
      checkPlayable();
      const interval = setInterval(checkPlayable, 3000);

      return () => clearInterval(interval);
    }
  }, [accessToken, playable, countryData, currentPlayerIsPlayingState]);
  // END 240214: VideoLiveDetail 에서 이전

  const capitalizeLetter = () => {
    // 종료: end, 송출중: streaming, 준비: ready, 임시중지: paused
    let liveStateText;

    if (!clipInfo) {
      return 'Loading...';
    }

    if (clipInfo?.live_status === 'streaming') {
      liveStateText = t('live_state_live');
      return liveStateText.toUpperCase();
    }

    if (clipInfo?.live_status === 'end') {
      liveStateText = t('live_state_ended');
    } else if (clipInfo?.live_status === 'ready') {
      liveStateText = t('live_state_expected');
    } else if (clipInfo?.live_status === 'paused') {
      liveStateText = t('live_state_ended');
    } else {
      liveStateText = t('live_state_replay');
    }
    // 첫 글자만 대문자
    return liveStateText.charAt(0).toUpperCase() + liveStateText.slice(1);
  };

  // 현재 url을 클립보드에 복사
  const copyUrlToClipboard = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        // 복사 성공 시 토스트 메시지 출력
        setIsToastShow(true);
      })
      .catch((err) => {
        // 복사 실패 시 에러 처리
        console.error('URL 복사 실패', err);
      });
  };

  // likeCount 조회(라이브 클립은 전역 관리, 일반 클립은 해당 함수 최초 렌더링 시 한번 호출)
  const fetchLikeCount = async (key_type = 'next') => {
    const type = 'live_clip';

    try {
      const response = await api.get(`live_chat/${type}/${type_id}?key_type=${key_type}`, {
        headers: {
          Authorization: `${accessToken}`,
        },
      });

      const fetchData = response?.data?.likeCount;
      setLocalLikeCount(fetchData);
    } catch (error) {
      const errorCode = error.response && error.response.data && error.response.data.code;
      if (
        errorCode === ERROR_CODES.invalidToken ||
        errorCode === ERROR_CODES.tokenExpired ||
        errorCode === ERROR_CODES.unauthorized
      ) {
        clearSession();
        navigate('/');
        setOtpLogin(true); // 로그인 모달
      } else {
        console.log(errorCode);
      }
    }
  };

  useEffect(() => {
    let timer;
    if (isToastShow) {
      timer = setTimeout(() => {
        setIsToastShow(false);
      }, 4000);
    }

    return () => clearTimeout(timer);
  }, [isToastShow]);

  useEffect(() => {
    fetchLikeCount();
  }, []);

  return (
    <>
      <div className={'ml-video-info-wrapper'}>
        {/* 종료: end, 송출중: streaming, 준비: ready, 임시중지: paused */}
        <p
          className={`live-state-text ${clipInfo?.live_status === 'streaming' && `is-live`} ${
            clipInfo?.live_status === 'ready' && `is-ready`
          }`}
        >
          {capitalizeLetter()}
        </p>
        <h3 className={'video-info-title text-ellipsis'}>{clipInfo?.cliptitle}</h3>
        <div className='video-info-bottom-group'>
          <div className='video-info-bottom-box'>
            {/* live는 시간까지, replay는 날짜까지 */}
            <span className={'bottom-info'}>
              {videoType === 'live' && liveClipInfo
                ? `${dayjs(liveClipInfo?.live_date).format('YYYY.MM.DD hh:mm')}(KST)`
                : dayjs(clipInfo?.broaddate).format('YYYY.MM.DD')}
            </span>
          </div>
          <div className='video-info-bottom-box'>
            <span className={'bottom-heart-icon-group'}>
              <i className={'icon icon-heart-gray'} />
            </span>
            {/* 라이브 클립은 전역 관리 데이터, 일반 클립 해당 컴포넌트 호출 데이터 바인딩 */}
            <span className={'bottom-info'}>{videoType === 'live' && liveClipInfo ? likeCount : localLikeCount}</span>
          </div>
          {/* live 플레이어에서만 노출 */}
          {videoType === 'live' && liveClipInfo ? (
            <div className='video-info-bottom-box'>
              <span className={'bottom-person-icon-group'}>
                <i className={'icon icon-person-filled'} />
              </span>
              <span className={'bottom-info'}>{liveUserCount}</span>
            </div>
          ) : null}
        </div>
        <div className='video-info-share-group'>
          <ShareButton buttonType={'type-02'} onClick={copyUrlToClipboard} />
        </div>
      </div>
      {isToastShow ? <Toast isShow={isToastShow} toastMsg={t('url_copy_msg')} /> : null}
    </>
  );
};

export default VideoInfoWrapper;
